import { ChangeEvent, ReactNode } from "react"
import { NavLink, Outlet, useNavigate } from "react-router-dom"
import classNames from "classnames"

import { ApiTypes, UITypes } from "types"

import { convertDataToOptions } from "utils/convertData"

import styles from "./index.module.scss"

export const accountTabs = [
  { label: "Profile", path: "/account/profile" },
  { label: "Additional Information", path: "/account/additional-information" },
  { label: "Password", path: "/account/password" },
  { label: "Themes", path: "/account/theme" },
]

export type ProfileValues = {
  companyName: string
  address1: string
  address2: string
  country: UITypes.Option[]
  zip: UITypes.Option[]
  state: string
  stateId?: number
  city: string
  cityId?: number
}

export type AdditionalValues = {
  email: string
  enterpriseUrl: string
  fullName: string
  phoneNumber: string
  image: File | undefined
}

type FieldProps = {
  label: string
  name: string
  value: string
  placeholder?: string
  type?: "password" | "text"
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  className?: string
}

export const Field = ({ label, name, value, placeholder, type = "text", onChange, className }: FieldProps) => (
  <label className={classNames(styles.field, className)}>
    <span className={styles.label}>{label}</span>
    <input
      className={styles.input}
      name={name}
      value={value}
      placeholder={placeholder}
      type={type}
      onChange={onChange}
    />
  </label>
)

export const AccountPageShell = ({ children, footer }: { children: ReactNode; footer?: ReactNode }) => (
  <section className={styles.panel}>
    <div className={styles.panelBody}>{children}</div>
    {footer}
  </section>
)

export const AccountPageLayout = () => (
  <main className={styles.page}>
    <nav className={styles.tabs} aria-label="Account settings">
      {accountTabs.map(tab => (
        <NavLink
          key={tab.path}
          className={({ isActive }) => classNames(styles.tab, { [styles.tabActive]: isActive })}
          to={tab.path}
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
    <Outlet />
  </main>
)

export const getEnterpriseProfileInitialState = (
  enterprise?: ApiTypes.Model.Enterprise,
  country?: ApiTypes.Model.Country | undefined,
): ProfileValues => ({
  companyName: enterprise?.name || "",
  address1: enterprise?.address1 || "",
  address2: enterprise?.address2 || "",
  country: country ? [{ value: country.id, label: country.name }] : [],
  zip: enterprise?.zip ? convertDataToOptions({ data: [enterprise.zip] }, "id", "code") : [],
  state: enterprise?.state?.name || "",
  stateId: enterprise?.state?.id,
  city: enterprise?.city?.name || "",
  cityId: enterprise?.city?.id,
})

export const getEnterpriseAdditionalInitialState = (enterprise?: ApiTypes.Model.Enterprise): AdditionalValues => ({
  email: enterprise?.email || "",
  enterpriseUrl: enterprise?.url || "",
  fullName: enterprise?.full_name || "",
  phoneNumber: enterprise?.tel1 || "",
  image: undefined,
})

export const appendEnterpriseValue = (formData: FormData, name: string, value?: string | number | boolean | null) => {
  if (value === undefined || value === null) return

  formData.append(name, String(value))
}

const normalizeUrl = (value?: string) => {
  if (!value) return value

  return /^(http:\/\/|https:\/\/)/.test(value) ? value : `https://${value}`
}

export const normalizeComparableUrl = (value?: string) => normalizeUrl(value?.trim()) || ""

export const prepareEnterpriseAccountFormData = ({
  enterprise,
  country,
  profileValues,
  additionalValues,
}: {
  enterprise: ApiTypes.Model.Enterprise
  country?: ApiTypes.Model.Country
  profileValues?: ProfileValues
  additionalValues?: AdditionalValues
}) => {
  const formData = new FormData()
  const hasProfileValues = Boolean(profileValues)
  const profile = profileValues ?? getEnterpriseProfileInitialState(enterprise, country)
  const additional = additionalValues ?? getEnterpriseAdditionalInitialState(enterprise)
  const normalizedUrl = normalizeComparableUrl(additional.enterpriseUrl)
  const countryValue = hasProfileValues ? (profile.country[0]?.value ?? "") : (country?.id ?? enterprise.country?.id)
  const zipValue = hasProfileValues ? (profile.zip[0]?.value ?? "") : enterprise.zip?.id
  const cityValue = hasProfileValues ? (profile.cityId ?? profile.city.trim()) : enterprise.city?.id
  const stateValue = hasProfileValues ? (profile.stateId ?? profile.state.trim()) : enterprise.state?.id

  appendEnterpriseValue(formData, "name", profile.companyName.trim())
  appendEnterpriseValue(formData, "address1", profile.address1.trim())
  appendEnterpriseValue(formData, "address2", profile.address2.trim())
  appendEnterpriseValue(formData, "country", countryValue)
  appendEnterpriseValue(formData, "zip", zipValue)
  appendEnterpriseValue(formData, "city", cityValue)
  appendEnterpriseValue(formData, "state", stateValue)
  appendEnterpriseValue(formData, "full_name", additional.fullName.trim())
  appendEnterpriseValue(formData, "email", additional.email.trim())
  appendEnterpriseValue(formData, "tel1", additional.phoneNumber.trim())
  appendEnterpriseValue(formData, "url", normalizedUrl)
  appendEnterpriseValue(formData, "is_enabled", enterprise.is_enabled ? 1 : 0)
  formData.append("contractor_ids", JSON.stringify(enterprise.contractor_ids ?? []))

  if (additional.image) {
    formData.append("image", additional.image)
  }

  return formData
}

export const isEnterpriseProfileDirty = (
  enterprise: ApiTypes.Model.Enterprise,
  country: ApiTypes.Model.Country | undefined,
  profileValues: ProfileValues,
) =>
  profileValues.companyName.trim() !== String(enterprise.name ?? "") ||
  profileValues.address1.trim() !== String(enterprise.address1 ?? "") ||
  profileValues.address2.trim() !== String(enterprise.address2 ?? "") ||
  String(profileValues.country[0]?.value ?? "") !== String(country?.id ?? enterprise.country?.id ?? "") ||
  String(profileValues.zip[0]?.value ?? "") !== String(enterprise.zip?.id ?? "") ||
  String(profileValues.cityId ?? "") !== String(enterprise.city?.id ?? "") ||
  String(profileValues.stateId ?? "") !== String(enterprise.state?.id ?? "") ||
  profileValues.city.trim() !== String(enterprise.city?.name ?? "") ||
  profileValues.state.trim() !== String(enterprise.state?.name ?? "")

export const isEnterpriseAdditionalDirty = (
  enterprise: ApiTypes.Model.Enterprise,
  additionalValues: AdditionalValues,
) =>
  additionalValues.fullName.trim() !== String(enterprise.full_name ?? "") ||
  additionalValues.email.trim() !== String(enterprise.email ?? "") ||
  additionalValues.phoneNumber.trim() !== String(enterprise.tel1 ?? "") ||
  normalizeComparableUrl(additionalValues.enterpriseUrl) !== normalizeComparableUrl(enterprise.url) ||
  Boolean(additionalValues.image)

export const AccountCancelButton = ({ disabled = false }: { disabled?: boolean }) => {
  const navigate = useNavigate()

  return (
    <button className={styles.button} type="button" onClick={() => navigate(-1)} disabled={disabled}>
      Cancel
    </button>
  )
}

export const Footer = ({
  form,
  isLoading,
  isSaveDisabled,
  onSave,
  saveButtonType = "button",
}: {
  form?: string
  isLoading: boolean
  isSaveDisabled: boolean
  onSave?: () => void
  saveButtonType?: "button" | "submit"
}) => (
  <div className={styles.footer}>
    <AccountCancelButton disabled={isLoading} />
    <button
      className={classNames(styles.button, styles.saveButton)}
      form={form}
      type={saveButtonType}
      onClick={onSave}
      disabled={isLoading || isSaveDisabled}
    >
      {isLoading ? "Saving..." : "Save"}
    </button>
  </div>
)
